import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { RolesPipe } from '@wilson/authorization';
import {
  Activity,
  ACTIVITY_MODAL_ROW_HEIGHT,
  AssignmentTimeRange,
  UserTimelinesModal,
} from '@wilson/interfaces';
import {
  FetchProfitabilityScoreSetting,
  ProfitabilityScoreState,
} from '@wilson/profitability-score/state';
import {
  GanttDimensionDataSourceService,
  ValidationDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ActivityDropZoneHelperService,
  TimelineModalFilterService,
  UpdateShiftOnActivityDragService,
} from '@wilson/shift-timeline/core';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';
import {
  MODAL_VIEW,
  SetTimelineModalFilterUsers,
  ShiftAssignmentModalState,
  TimelineFetchActivitiesAssignment,
  TimelineModalFetchUsers,
  TimelineModalFilterState,
  TimelineModalSetDaysRange,
  TimelineModalSetTimeframeWidth,
  ToggleFilterLoading,
} from '@wilson/shift-timeline/state';
import { add, endOfDay, startOfDay, sub } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, combineLatest, firstValueFrom, map, of, tap } from 'rxjs';
import { FindStaffDataService } from '../services/find-staff-data.service';
import { FindStaffGanttChartService } from '../services/find-staff-gantt-chart.service';
import { FindStaffPopupHelperService } from '../services/find-staff-popup-helper.service';
import { FindStaffValidationDataSourceService } from '../services/find-staff-validation-data-source.service';
import { GanttModalBaseService } from '../services/gantt-modal-base.service';
import { TimelineModalDimensionDataSourceService } from '../services/timeline-modal-dimension-data-source.service';

@Component({
  selector: 'wilson-find-activity-staff-popup',
  templateUrl: './find-activity-staff-popup.component.html',
  styleUrls: ['./find-activity-staff-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TimelineModalFilterService,
    CreateNewViaDragAndDropService,
    ActivityDropZoneHelperService,
    FindStaffGanttChartService,
    FindStaffDataService,
    {
      provide: ValidationDataSourceService,
      useClass: FindStaffValidationDataSourceService,
    },
    {
      provide: GanttDimensionDataSourceService,
      useClass: TimelineModalDimensionDataSourceService,
    },
    UpdateShiftOnActivityDragService,
  ],
  standalone: false,
})
export class FindActivityStaffPopupComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly toastr = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private ganttModalBaseService = inject(GanttModalBaseService);
  private findStaffPopupHelperService = inject(FindStaffPopupHelperService);
  private nzModalService = inject(NzModalService);
  rolesPipe = inject(RolesPipe);
  private findStaffGanttChartService = inject(FindStaffGanttChartService);
  private findStaffDataService = inject(FindStaffDataService);

  @ViewChild('findStaffWrapper', { static: true })
  findStaffWrapper!: ElementRef<HTMLDivElement>;
  userTimelinesInfo$!: Observable<UserTimelinesModal[]>;
  public isFetchedShiftAssignmentSuccessful$!: Observable<boolean>;
  public faExchange = faExchangeAlt;
  hasCompanyAdminRole$: Observable<boolean> = of(false);
  hasViewRoles$: Observable<boolean> = of(false);
  isEnabledProfitabilityScores$ = this.store.select(
    ProfitabilityScoreState.enableSettings,
  );

  async ngOnInit(): Promise<void> {
    this.store.dispatch(
      new TimelineModalSetTimeframeWidth(MODAL_VIEW.ACTIVITY),
    );
    this.findStaffGanttChartService.setViewAndHoursRange(MODAL_VIEW.ACTIVITY);
    this.findStaffGanttChartService.setDefaultRowHeight(
      ACTIVITY_MODAL_ROW_HEIGHT,
    );
    this.findStaffGanttChartService.setHolidaysRegion(
      this.store.selectSnapshot(AuthState.publicHolidaysRegion),
    );

    await firstValueFrom(
      this.findStaffDataService.timelineFetchUsersOvertimes(),
    );

    this.hasCompanyAdminRole$ = this.rolesPipe.transform(['company-admin']);
    this.hasViewRoles$ = this.rolesPipe.transform([
      'dispatcher-personnel',
      'dispatcher-operations',
    ]);

    combineLatest([
      this.hasCompanyAdminRole$,
      this.hasViewRoles$,
      this.isEnabledProfitabilityScores$,
    ])
      .pipe(
        map(
          ([
            hasCompanyAdminRole,
            hasViewRoles,
            isEnabledProfitabilityScores,
          ]) => {
            if (
              isEnabledProfitabilityScores &&
              (hasCompanyAdminRole || hasViewRoles)
            ) {
              this.store.dispatch(new FetchProfitabilityScoreSetting());
            }
          },
        ),
      )
      .subscribe();

    this.ganttModalBaseService.findStaffWrapper = this.findStaffWrapper;
    this.ganttModalBaseService.isAssignStaffModal = false;

    this.isFetchedShiftAssignmentSuccessful$ = this.store.select(
      ShiftAssignmentModalState.isFetchedShiftAssignmentSuccessful,
    );

    await this.loadUsersForActivitiesAssignment(1);
  }

  private async loadUsersForActivitiesAssignment(
    numberOfDays = 3,
  ): Promise<void> {
    const activitiesToAssign = this.store.selectSnapshot(
      ShiftAssignmentModalState.activitiesToAssign,
    );

    const assignmentTimeRange: AssignmentTimeRange = {
      start: activitiesToAssign[0].startDatetime,
      end: activitiesToAssign[activitiesToAssign.length - 1].endDatetime,
    };

    this.filterUsers();

    const startDate = sub(new Date(assignmentTimeRange.start), {
      days: numberOfDays,
    });
    const endDate = add(new Date(assignmentTimeRange.start), {
      days: numberOfDays,
    });
    await this.setTimelineDataAndWindowWidthForActivitiesAssignment(
      startDate,
      endDate,
      activitiesToAssign,
      assignmentTimeRange,
    );
  }

  private async setTimelineDataAndWindowWidthForActivitiesAssignment(
    startDate: Date,
    endDate: Date,
    activitiesToAssign: Activity[],
    assignmentTimeRange: AssignmentTimeRange,
  ): Promise<void> {
    const timeframe = {
      start: startOfDay(startDate),
      end: endOfDay(endDate),
    };
    this.store.dispatch(new TimelineModalSetDaysRange(timeframe));

    const fetchUserActions = this.store
      .dispatch([new TimelineModalFetchUsers(timeframe, assignmentTimeRange)])
      .pipe(
        tap(() => {
          this.store.dispatch(
            new SetTimelineModalFilterUsers(
              this.store.selectSnapshot(
                ShiftAssignmentModalState.timelineModalUsers,
              ),
            ),
          );
        }),
      );

    const activityIdsToAssign = activitiesToAssign.map(
      (activity) => activity.id,
    ) as string[];
    const fetchActivitiesAssignmentsAction = this.store
      .dispatch(new TimelineFetchActivitiesAssignment(activityIdsToAssign))
      .pipe(
        tap(() => {
          this.ganttModalBaseService.updateModalWidth();
        }),
      );

    try {
      await firstValueFrom(fetchActivitiesAssignmentsAction);
      await firstValueFrom(fetchUserActions);
    } catch {
      this.toastr.error(this.translate.instant('general.something_went_wrong'));
      this.nzModalService.closeAll();
    }
  }

  private filterUsers(): void {
    this.userTimelinesInfo$ = this.store
      .select(TimelineModalFilterState.activityAssignmentModalFilteredUsers())
      .pipe(
        map((filteredUsers) => {
          return this.findStaffPopupHelperService.sortStaffSearchResults(
            filteredUsers,
          );
        }),
        tap(() => {
          setTimeout(() => {
            this.store.dispatch(new ToggleFilterLoading(false));
          });
        }),
      );
  }
}
